import React, { Fragment, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Download } from "lucide-react";
import { formatINR } from "../../utils/formatINR";
import { ISSUE_LABELS, RECON_STATUS, csvAmount, downloadCSV, formatDay, formatWeekday } from "../../utils/finance";

const OTHER = "__other__";

const StatusBadge = ({ status }) => {
  const s = RECON_STATUS[status] || RECON_STATUS.insufficient_data;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${s.className}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// a figure that opens its underlying records
const Amount = ({ value, onClick, className = "", muted }) => {
  const text = formatINR(value);
  if (!onClick || !value) {
    return <span className={muted || !value ? "text-neutral-400" : className}>{text}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-1 -mx-1 tabular-nums underline-offset-2 hover:bg-neutral-100 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${className}`}
    >
      {text}
    </button>
  );
};

export default function DailyFlowTable({ days, groups, totals, range, onOpenRecords }) {
  // groups shown as their own column; the rest are summed into "Other groups"
  const [shownGroups, setShownGroups] = useState(null); // null = all
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [expanded, setExpanded] = useState(() => new Set());

  const groupsWithMoney = useMemo(() => {
    const paid = new Set();
    days.forEach((d) => Object.keys(d.settlements_by_group || {}).forEach((g) => d.settlements_by_group[g] && paid.add(g)));
    return groups.filter((g) => paid.has(g.group_id));
  }, [days, groups]);

  const visible = shownGroups ? groupsWithMoney.filter((g) => shownGroups.has(g.group_id)) : groupsWithMoney;
  const hasOther = visible.length < groupsWithMoney.length;

  const groupValue = (day, gid) => {
    const byGroup = day.settlements_by_group || {};
    if (gid !== OTHER) return byGroup[gid] || 0;
    const shown = new Set(visible.map((g) => g.group_id));
    return Object.entries(byGroup).reduce((sum, [g, v]) => (shown.has(g) ? sum : sum + v), 0);
  };

  const sorted = useMemo(() => {
    const list = [...days];
    const val = (d) => (sort.key === "date" ? d.date : d[sort.key]);
    list.sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const cmp = x < y ? -1 : x > y ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [days, sort]);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  const toggleExpanded = (date) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(date) ? next.delete(date) : next.add(date);
      return next;
    });

  const toggleGroup = (gid) =>
    setShownGroups((prev) => {
      const next = new Set(prev || groupsWithMoney.map((g) => g.group_id));
      next.has(gid) ? next.delete(gid) : next.add(gid);
      return next.size === groupsWithMoney.length ? null : next;
    });

  const columnTotal = (gid) => days.reduce((sum, d) => sum + groupValue(d, gid), 0);

  const exportCSV = () => {
    const groupCols = groups.map((g) => g.group_id);
    downloadCSV(`daily-money-flow_${range.from}_to_${range.to}.csv`, [
      ["Date", "Money received", "Payments", "Gateway fees", ...groups.map((g) => `Settlement: ${g.group_name}`), "Total settlements", "Refunds", "Refunds pending", "Tetra net retained", "Status", "Difference"],
      ...[...days].sort((a, b) => (a.date < b.date ? -1 : 1)).map((d) => [
        d.date,
        csvAmount(d.received),
        d.payments,
        csvAmount(d.gateway_fees),
        ...groupCols.map((g) => csvAmount((d.settlements_by_group || {})[g] || 0)),
        csvAmount(d.settlements),
        csvAmount(d.refunds),
        csvAmount(d.refunds_pending),
        csvAmount(d.net_retained),
        RECON_STATUS[d.status]?.label || d.status,
        csvAmount(d.difference),
      ]),
    ]);
  };

  const SortHeader = ({ k, children, right, className = "" }) => (
    <th className={`px-2.5 py-2.5 whitespace-nowrap ${right ? "text-right" : "text-left"} ${className}`}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${sort.key === k ? "text-ink" : "text-neutral-500"} hover:text-ink`}
      >
        {children}
        {sort.key === k && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
  const PlainHeader = ({ children, right, title, wrap }) => (
    <th title={title} className={`px-2.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 ${wrap ? "min-w-[6.5rem] max-w-[9rem] leading-tight" : "whitespace-nowrap"} ${right ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );

  const colCount = 7 + visible.length + (hasOther ? 1 : 0);

  return (
    <section className="card overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-neutral-200 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <div>
          <h2 className="section-title">Daily money flow</h2>
          <p className="mt-0.5 text-[13px] text-neutral-500">
            Click any amount to see the records behind it. Days are Indian calendar days (IST).
          </p>
        </div>
        <button onClick={exportCSV} className="btn btn-secondary self-start text-[13px]" disabled={!days.length}>
          <Download className="h-4 w-4" />
          Export CSV
        </button>
      </div>

      {groupsWithMoney.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-neutral-100 px-4 py-3 sm:px-5">
          <span className="text-[12px] font-medium text-neutral-500">Group columns:</span>
          {groupsWithMoney.map((g) => {
            const on = !shownGroups || shownGroups.has(g.group_id);
            return (
              <button
                key={g.group_id}
                type="button"
                onClick={() => toggleGroup(g.group_id)}
                aria-pressed={on}
                className={`rounded-full border px-2.5 py-1 text-[12px] font-medium transition ${
                  on ? "border-ink bg-ink text-white" : "border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
                }`}
              >
                {g.group_name}
              </button>
            );
          })}
          {shownGroups && (
            <button type="button" onClick={() => setShownGroups(null)} className="text-[12px] font-medium text-brand-600 hover:underline">
              Show all
            </button>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className="bg-neutral-50">
            <tr className="border-b border-neutral-200">
              <th className="w-8" />
              <SortHeader k="date">Date</SortHeader>
              <PlainHeader>Status</PlainHeader>
              <SortHeader k="received" right>Received</SortHeader>
              <PlainHeader right wrap title="Razorpay fee on the day's payments, including GST">Gateway fees</PlainHeader>
              {visible.map((g) => (
                <PlainHeader key={g.group_id} right wrap>{g.group_name}</PlainHeader>
              ))}
              {hasOther && <PlainHeader right>Other groups</PlainHeader>}
              <SortHeader k="settlements" right>Settlements</SortHeader>
              <SortHeader k="refunds" right>Refunds</SortHeader>
              <SortHeader k="net_retained" right className="sticky right-0 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)] bg-neutral-50">Tetra retained</SortHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {sorted.map((d) => {
              const open = expanded.has(d.date);
              return (
                <Fragment key={d.date}>
                  <tr className={open ? "bg-neutral-50" : "hover:bg-neutral-50/60"}>
                    <td className="pl-3">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(d.date)}
                        aria-expanded={open}
                        aria-label={`Details for ${formatDay(d.date)}`}
                        className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-ink"
                      >
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-2.5 py-2.5 whitespace-nowrap">
                      <span className="font-medium text-ink">{formatDay(d.date)}</span>
                      <span className="block text-[11px] text-neutral-400">{formatWeekday(d.date)}</span>
                    </td>
                    <td className="px-2.5 py-2.5">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-2.5 py-2.5 text-right tabular-nums font-semibold">
                      <Amount value={d.received} className="text-green-700" onClick={() => onOpenRecords({ kind: "received", date: d.date })} />
                    </td>
                    <td className="px-2.5 py-2.5 text-right tabular-nums text-neutral-600">
                      <Amount value={d.gateway_fees} className="text-neutral-700" onClick={() => onOpenRecords({ kind: "received", date: d.date })} />
                    </td>
                    {visible.map((g) => (
                      <td key={g.group_id} className="px-2.5 py-2.5 text-right tabular-nums">
                        <Amount
                          value={groupValue(d, g.group_id)}
                          className="text-red-700"
                          onClick={() => onOpenRecords({ kind: "settlements", date: d.date, groupId: g.group_id })}
                        />
                      </td>
                    ))}
                    {hasOther && (
                      <td className="px-2.5 py-2.5 text-right tabular-nums">
                        <Amount value={groupValue(d, OTHER)} className="text-red-700" onClick={() => onOpenRecords({ kind: "settlements", date: d.date })} />
                      </td>
                    )}
                    <td className="px-2.5 py-2.5 text-right tabular-nums font-medium">
                      <Amount value={d.settlements} className="text-red-700" onClick={() => onOpenRecords({ kind: "settlements", date: d.date })} />
                    </td>
                    <td className="px-2.5 py-2.5 text-right tabular-nums">
                      <Amount value={d.refunds} className="text-red-700" onClick={() => onOpenRecords({ kind: "refunds", date: d.date })} />
                      {d.refunds_pending > 0 && <div className="text-[11px] text-amber-700">{formatINR(d.refunds_pending)} pending</div>}
                    </td>
                    <td className={`px-2.5 py-2.5 text-right tabular-nums sticky right-0 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)] ${open ? "bg-neutral-50" : "bg-white [tr:nth-child(even)_&]:bg-neutral-100"}`}>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(d.date)}
                        className={`rounded px-1 -mx-1 font-semibold hover:bg-neutral-100 ${d.net_retained < 0 ? "text-red-700" : "text-ink"}`}
                      >
                        {formatINR(d.net_retained)}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-neutral-50">
                      <td />
                      <td colSpan={colCount - 1} className="px-3 pb-4 pt-1">
                        <DayDetail day={d} groups={groups} onOpenRecords={onOpenRecords} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!sorted.length && (
              <tr>
                <td colSpan={colCount} className="px-3 py-10 text-center text-neutral-500">No days in this range.</td>
              </tr>
            )}
          </tbody>
          {sorted.length > 1 && (
            <tfoot className="border-t-2 border-neutral-200 bg-neutral-50 font-semibold">
              <tr>
                <td />
                <td className="px-2.5 py-2.5">Total</td>
                <td />
                <td className="px-2.5 py-2.5 text-right tabular-nums text-green-700">{formatINR(totals.received)}</td>
                <td className="px-2.5 py-2.5 text-right tabular-nums text-neutral-700">{formatINR(totals.gateway_fees)}</td>
                {visible.map((g) => (
                  <td key={g.group_id} className="px-2.5 py-2.5 text-right tabular-nums text-red-700">{formatINR(columnTotal(g.group_id))}</td>
                ))}
                {hasOther && <td className="px-2.5 py-2.5 text-right tabular-nums text-red-700">{formatINR(columnTotal(OTHER))}</td>}
                <td className="px-2.5 py-2.5 text-right tabular-nums text-red-700">{formatINR(totals.settlements)}</td>
                <td className="px-2.5 py-2.5 text-right tabular-nums text-red-700">{formatINR(totals.refunds)}</td>
                <td className={`px-2.5 py-2.5 text-right tabular-nums sticky right-0 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)] bg-neutral-50 ${totals.net_retained < 0 ? "text-red-700" : "text-ink"}`}>{formatINR(totals.net_retained)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function DayDetail({ day, groups, onOpenRecords }) {
  const names = Object.fromEntries(groups.map((g) => [g.group_id, g.group_name]));
  const paidGroups = Object.entries(day.settlements_by_group || {}).filter(([, v]) => v);
  return (
    <div className="grid gap-4 rounded-lg border border-neutral-200 bg-white p-4 lg:grid-cols-2">
      <div>
        <p className="field-label mb-2">How Tetra retained is calculated</p>
        <dl className="space-y-1 text-[13px] tabular-nums">
          <Row label={`Money received (${day.payments} payment${day.payments === 1 ? "" : "s"})`} value={formatINR(day.received)} className="text-green-700" />
          <Row label="− Razorpay gateway fees (incl. GST)" value={formatINR(day.gateway_fees)} />
          {paidGroups.map(([g, v]) => (
            <Row key={g} label={`− Settlement to ${names[g] || `Group ${g}`}`} value={formatINR(v)} className="text-red-700" />
          ))}
          {!paidGroups.length && <Row label="− Group settlements" value={formatINR(0)} />}
          <Row label="− Refunds processed" value={formatINR(day.refunds)} className="text-red-700" />
          <div className="border-t border-neutral-200 pt-1">
            <Row label="= Tetra net retained" value={formatINR(day.net_retained)} className="font-semibold text-ink" />
          </div>
        </dl>
        {day.refunds_pending > 0 && (
          <p className="mt-2 text-[12px] text-amber-700">{formatINR(day.refunds_pending)} of refunds were still pending at Razorpay and are not deducted.</p>
        )}
      </div>
      <div>
        <p className="field-label mb-2">Reconciliation</p>
        {day.issues.length === 0 ? (
          <p className="text-[13px] text-neutral-600">
            {day.status === "pending"
              ? "Every payment matches a balance credit. Some settlements or refunds for this day are still in progress."
              : "Every Razorpay payment matches a balance credit in the CMS, and every refund is recorded."}
          </p>
        ) : (
          <>
            <p className="mb-2 text-[13px] text-neutral-600">
              Difference between what the CMS credited and what Razorpay received:{" "}
              <span className={`whitespace-nowrap font-semibold tabular-nums ${day.difference === 0 ? "text-ink" : "text-red-700"}`}>{formatINR(day.difference)}</span>
            </p>
            <ul className="space-y-1.5">
              {day.issues.slice(0, 8).map((is) => (
                <li key={is.kind + is.reference} className="rounded-md border border-red-100 bg-red-50/60 px-2.5 py-1.5 text-[12px]">
                  <span className="font-medium text-red-800">{ISSUE_LABELS[is.kind] || is.kind}</span>
                  <span className="ml-1.5 font-mono text-[11px] text-neutral-600">{is.reference}</span>
                  <span className="ml-1.5 tabular-nums text-neutral-700">
                    {is.kind === "refund_not_recorded" ? formatINR(is.amount) : `received ${formatINR(is.expected)}, credited ${formatINR(is.actual)}`}
                  </span>
                  {is.detail && <div className="text-[11px] text-neutral-500">{is.detail}</div>}
                </li>
              ))}
            </ul>
            {day.issues.length > 8 && <p className="mt-1 text-[12px] text-neutral-500">+{day.issues.length - 8} more</p>}
            <button type="button" onClick={() => onOpenRecords({ kind: "received", date: day.date })} className="mt-2 text-[12px] font-medium text-brand-600 hover:underline">
              View all payments for this day
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, className = "" }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-neutral-600">{label}</dt>
    <dd className={className}>{value}</dd>
  </div>
);
